"use strict";
/**
 * Field coercion / extraction helpers for the Use Case Details info tab.
 *
 * The use-case payload pulls fields from heterogeneous source systems
 * (SharePoint people pickers, Azure DevOps identity refs, plain ADO
 * fields, etc.). This module flattens them into display-ready strings so
 * the Markdown renderer never produces `[object Object]` or raw JSON.
 */
Object.defineProperty(exports, "__esModule", { value: true });
exports.formatCreatedAt = exports.getTargetSystemId = exports.getOutputFieldValue = exports.coerceFieldValueToString = void 0;
var markdown_text_1 = require("./markdown-text");
var PERSON_DISPLAY_KEYS = [
    'Title',
    'title',
    'DisplayName',
    'displayName',
    'Name',
    'name',
    'FullName',
    'fullName',
    'UserName',
    'userName',
    'Email',
    'email',
    'EmailAddress',
    'emailAddress',
];
/**
 * Coerce an arbitrary field value (string, number, person-object, array of
 * those) into a human-readable string. Avoids "[object Object]" for
 * SharePoint-style people fields like `{ Title, Email, ... }` and also
 * handles values that arrive as JSON-encoded strings of the same shape
 * (e.g. Azure DevOps identity refs).
 */
exports.coerceFieldValueToString = function (value) {
    if (value === null || value === undefined) {
        return '';
    }
    if (typeof value === 'string') {
        var trimmed = value.trim();
        // If the string itself is a JSON-encoded object/array, parse it once
        // and re-coerce so we extract the display name instead of showing
        // raw JSON.
        if ((trimmed.startsWith('{') && trimmed.endsWith('}')) ||
            (trimmed.startsWith('[') && trimmed.endsWith(']'))) {
            try {
                var fromParsed = exports.coerceFieldValueToString(JSON.parse(trimmed));
                if (fromParsed.trim()) {
                    return fromParsed;
                }
            }
            catch (_a) {
                // Fall through and return the original string.
            }
        }
        return value;
    }
    if (typeof value === 'number' || typeof value === 'boolean') {
        return "" + value;
    }
    if (Array.isArray(value)) {
        return value
            .map(function (item) { return exports.coerceFieldValueToString(item); })
            .filter(function (part) { return part.trim().length > 0; })
            .join(', ');
    }
    if (typeof value === 'object') {
        var obj = value;
        for (var _i = 0, PERSON_DISPLAY_KEYS_1 = PERSON_DISPLAY_KEYS; _i < PERSON_DISPLAY_KEYS_1.length; _i++) {
            var key = PERSON_DISPLAY_KEYS_1[_i];
            var candidate = obj[key];
            if (typeof candidate === 'string' && candidate.trim()) {
                return candidate;
            }
            if (typeof candidate === 'number') {
                return "" + candidate;
            }
        }
        return '';
    }
    return '';
};
/**
 * Read the first non-empty value from a list of candidate field names on
 * the use-case output payload, applying {@link coerceFieldValueToString}.
 */
exports.getOutputFieldValue = function (output, fieldNames) {
    var _a;
    var fields = (_a = output === null || output === void 0 ? void 0 : output.AiUseCase) === null || _a === void 0 ? void 0 : _a.fields;
    if (!fields) {
        return '';
    }
    for (var _i = 0, fieldNames_1 = fieldNames; _i < fieldNames_1.length; _i++) {
        var fieldName = fieldNames_1[_i];
        var stringValue = exports.coerceFieldValueToString(fields[fieldName]);
        if (stringValue.trim()) {
            return stringValue;
        }
    }
    return '';
};
/**
 * Resolve the target system id for the info-tab summary table, falling
 * back to the system-information map when the draft does not include it.
 */
exports.getTargetSystemId = function (output) {
    var _a, _b;
    if ((_a = output === null || output === void 0 ? void 0 : output.Draft) === null || _a === void 0 ? void 0 : _a.TargetSystemID) {
        return output.Draft.TargetSystemID;
    }
    var systemInformation = (_b = output === null || output === void 0 ? void 0 : output.AiUseCase) === null || _b === void 0 ? void 0 : _b.systemInformation;
    if (!systemInformation) {
        return '-';
    }
    var targetSystem = Object.keys(systemInformation)
        .map(function (key) { return systemInformation[key]; })
        .find(function (item) { return item.type === 'target'; });
    return (targetSystem === null || targetSystem === void 0 ? void 0 : targetSystem.id) || '-';
};
/**
 * Render an epoch timestamp as an ISO-style YYYY-MM-DD date, or `-` when
 * absent / unparseable.
 */
exports.formatCreatedAt = function (value) {
    var timestamp = Number(value);
    if (!timestamp) {
        return '-';
    }
    var date = new Date(timestamp);
    if (Number.isNaN(date.getTime())) {
        return markdown_text_1.normalizeReportText(value);
    }
    return date.toISOString().slice(0, 10);
};
